import { z } from 'zod';
import { PendingEmailChallengeSchema } from './email-verification.js';
import { MeterDetailSchema } from './meter.js';
import { DepositGateSchema } from './sign.js';
/**
 * ── `GET /bff/funnel` — THE REHYDRATE SNAPSHOT ──────────────────────────────
 *
 * The bug this fixes: typing into the enrollment funnel and refreshing the
 * browser lost everything. It was NEVER a persistence bug — the BFF has held the
 * funnel state server-side, keyed by the signed `enr_sid` cookie, since E-SF01
 * (`session/store.ts`). What was missing is a way to READ IT BACK. `FunnelProvider`'s
 * own header promised "the server session is what a returning tab redeems from";
 * the redemption path was never built. This is that path.
 *
 * ── IT IS A READ. IT MINTS, RESOLVES AND COSTS NOTHING ─────────────────────
 * No upstream call, no session mint, no side effect: one funnel-session read,
 * reshaped. That is why it is NOT captcha-gated and NOT in the BFF's
 * `SENSITIVE_ROUTE_PATTERNS` — a CAPTCHA on a page-load rehydrate would break
 * every refresh, i.e. exactly the bug being fixed. It is covered by the baseline
 * per-IP (60/min) and per-session (30/min) rules like any other `/bff/*` request.
 *
 * ── WHAT IS DELIBERATELY *NOT* HERE, AND WHY (read before adding a field) ───
 * This response is the narrowest thing that restores NAVIGATION, not the funnel's
 * whole server state. Every omission below is load-bearing:
 *
 *   `tenantId`                  AC1c. The BFF never echoes it; the boot-time
 *                               response guard refuses to start on it.
 *   `ssn`                       Never persisted anywhere, by design. It transits
 *                               to Accounts server-side and is discarded. The
 *                               response guard refuses boot on the field name.
 *                               ⚠️ IT IS EXPECTED TO VANISH ON REFRESH — that is
 *                               the promise the credit page makes the customer.
 *   `dateOfBirth`               Response guard, no `allowOn` exception. The
 *                               customer retypes it. Do not fight that guard.
 *   `firstName`/`lastName`/     `customer-name-session.ts` and `contact-session.ts`
 *   `phone`                     both state "no response schema carries" these.
 *                               Since the SSN *and* the DOB must be retyped
 *                               anyway, rehydrating 4 of 6 credit fields buys
 *                               little and reverses two documented invariants.
 *   `esiId` / `esi_id`          Response guard: the raw ESIID must never egress
 *                               under its server-side field name.
 *   `monthlyUsageKwh`,          Not stored server-side AT ALL — no `data` key
 *   `usageTier`                 holds them. They are pure client display state and
 *                               now ride the URL (`/shop/plans?usage=&tier=`),
 *                               which survives refresh AND bookmarking.
 *   `prospectUid`, `quoteId`,   Server-side handles. `draft-quote-session.ts`,
 *   `accountUid`                `prospect-session.ts` and `sign-session.ts` all
 *                               state they never reach the browser.
 *
 * ── THE ONE PII FIELD THAT *IS* HERE: `verifiedEmail` (a recorded reversal) ─
 * `contact-session.ts` says the captured contact is "never echoed". This response
 * reverses that for ONE field, deliberately, and the reason is not convenience:
 * the SPA's submit gate tracks the PROVEN ADDRESS, not a boolean
 * (`credit.service.ts` — `IdentityForm.verifiedEmail`), precisely so that editing
 * the email field re-blocks submission. Without the address back, a refresh forces
 * a FRESH OTP SEND, which burns one of three per hour on BOTH the session ledger
 * (`email-verification-session.ts` MAX_SENDS_PER_WINDOW) and the victim-side
 * recipient cap (`EMAIL_VERIFICATION_RECIPIENT_RULE`). So a refresh would cost the
 * customer a third of their hourly budget for pressing F5. It is the customer's own
 * address, returned only to their own signed session, over TLS, on a
 * `credentials: 'same-origin'` fetch.
 */
/**
 * The furthest funnel step the SERVER can EVIDENCE from its own records.
 *
 * ⚠️ "Evidence" is the operative word — this is not the SPA's route. It is derived
 * from what the funnel session actually contains (a resolved candidate, a proven
 * email, a created prospect, a DRAFT quote, an accept result). The SPA uses it to
 * decide where a returning tab may legitimately resume, so a client that lies
 * about its progress cannot skip a step: the server never saw the evidence.
 *
 *   `meter`    — nothing resolved yet; start at the landing chooser.
 *   `plan`     — a meter is resolved; pick a plan.
 *   `credit`   — the email OTP has been proven for this session (the customer is
 *                mid-credit-step) but no credit decision has run.
 *   `estimate` — a prospect exists, so the credit decision has run.
 *   `sign`     — a DRAFT quote exists, so the estimate step completed.
 *   `signed`   — the at-accept saga completed; the enrollment is signed.
 */
export const FunnelStep = z.enum(['meter', 'plan', 'credit', 'estimate', 'sign', 'signed']);
/**
 * The resolved meter a returning tab redisplays: the OPAQUE session-scoped handle
 * plus the display-only `MeterDetail`.
 *
 * ⚠️ `MeterDetail.esiid` is why `GET /bff/funnel` needs the fourth entry in the
 * response guard's `esiid` `allowOn` set (`http/response-guard.ts`). That is not a
 * loophole and it must not be worked around by weakening the guard: the guard is
 * boot-refusing BY DESIGN, so the exception is reviewed once, in one place, with
 * its justification in the `reason` string. The value returned here is strictly
 * weaker than `POST /bff/esiid/lookup`'s — this route takes no input, resolves
 * nothing, and can only hand back the one meter this session already resolved
 * through the captcha-gated lookup. It creates no oracle.
 */
export const FunnelCandidateSchema = z.object({
    /** Opaque, session-scoped redemption handle (E-SF03 AC3). Never the raw ESI ID. */
    candidateId: z.string().min(1),
    /** Display-only meter card fields — the same shape `/bff/esiid/lookup` returned. */
    meter: MeterDetailSchema,
});
/**
 * `GET /bff/funnel` response body.
 *
 * Every field is nullable-or-derived so a brand-new session (cookie present, no
 * progress) is a valid 200 with an empty snapshot — NOT an error. The three states
 * the SPA must render are: 401 (no session at all), 200-with-nulls (session, no
 * progress) and 200-populated. A 401 is an ANSWER, not a failure, and it must
 * never trigger a session mint (`bff-client.ts` — `isSessionExpired`).
 */
export const FunnelSnapshotResponseSchema = z.object({
    /** Tenant slug, mirroring `SessionResponseSchema.slug`. Already public. */
    slug: z.string().min(1),
    /** ISO-8601 with offset. Absolute session expiry, mirroring `SessionResponse`. */
    expiresAt: z.string().min(1),
    /** The furthest step the server can evidence. See `FunnelStep`. */
    step: FunnelStep,
    /** The resolved meter + opaque handle, or null when nothing is resolved. */
    candidate: FunnelCandidateSchema.nullable(),
    /**
     * The address the E-SF05 OTP has PROVEN for this session, or null. The ONE PII
     * field crossing this boundary — see the file header for the justification.
     */
    verifiedEmail: z.string().email().nullable(),
    /**
     * The re-typeable identity fields the server already holds, so a refresh does not
     * make the customer enter them twice.
     *
     * ⚠️ THIS REVERSES a documented invariant. `customer-name-session.ts` and
     * `contact-session.ts` each said "no response schema carries" these, on the
     * reasoning that since SSN and DOB must be retyped anyway, restoring the rest
     * bought little. In practice a refresh that clears a customer's own name, email
     * and phone mid-enrollment reads as data loss and costs completions, so the
     * trade was re-decided. Those two comments were updated to match — do not
     * restore them from git history without also removing these fields.
     *
     * ⚠️ `dateOfBirth` IS NOT HERE, though `CustomerName` carries it server-side.
     * `response-guard.ts` bans `ssn`/`dateOfBirth`/`dob` on EVERY response with no
     * `allowOn` escape, and that guard REFUSES BOOT rather than warning. Adding the
     * DOB here does not "just work" — it stops the service starting, by design. The
     * customer retypes their DOB and SSN. That is the deliberate floor.
     */
    identity: z
        .object({
        firstName: z.string().min(1).nullable(),
        lastName: z.string().min(1).nullable(),
        /** Contact phone as the customer entered it (NANP digits). */
        phone: z.string().min(1).nullable(),
        /**
         * The email the customer gave at the contact step, as typed and UNPROVEN.
         * `verifiedEmail` above stays the only PROVEN address; this one exists so
         * the credit step can prefill the field instead of asking twice. The OTP
         * gate still applies to it exactly as to a freshly typed address.
         */
        email: z.string().min(1).nullable(),
    })
        .nullable(),
    /**
     * What this session already knows because an AGENT sent the customer here, or
     * null for an ordinary self-serve visit.
     *
     * ── WHY THIS IS TWO SMALL FIELDS AND NOT AN IDENTITY BLOCK ────────────────
     * The customer arrived holding a link out of an email inbox. That token proves
     * possession of the link and nothing else, so what crosses this boundary is
     * held to what a holder may safely see about the person the link was addressed
     * to: whether we already have them on file, and a display name.
     *
     * ⚠️ THERE IS NO ACCOUNT ID HERE, and there must not be. The BFF holds only a
     * boolean server-side, and the sign saga re-reads the real account from
     * Quotation with its own credential at the moment it acts.
     *
     * ⚠️ AND NO SSN, DOB, EMAIL, PHONE OR ADDRESS. Quotation's identity read
     * deliberately carries none of them, which is why the credit step still
     * collects what it always did — see `customerDisplayName`.
     */
    offer: z
        .object({
        /**
         * The quote already carries an Accounts account (the agent linked one).
         *
         * A SKIP CONDITION, not an instruction: it lets the funnel stop asking a
         * known customer for what we already hold. It does NOT remove a step —
         * `FunnelStep` order is unchanged and a skipped step is SATISFIED, so the
         * sequence guard still runs.
         */
        hasAccount: z.boolean(),
        /**
         * Quotation's PRIVACY-SAFE display name, or null.
         *
         * ⚠️ RENDER-ONLY. It is safe to greet someone with and unsafe to sign
         * anything with: the funnel's own name field becomes the OFAC `fullName`
         * at account create and the ESIGN `signerName` on the contract, so a
         * customer still types their legal name. Do not prefill that field from
         * this one.
         */
        customerDisplayName: z.string().min(1).nullable(),
    })
        .nullable(),
    /**
     * The server-authoritative deposit gate for the resolved candidate, reusing the
     * exact shape `GET /bff/sign/context` already returns. Null when there is no
     * resolved candidate to key a deposit record by.
     */
    depositGate: DepositGateSchema.nullable(),
    /**
     * An email OTP challenge that is STILL LIVE for this session, or null (E-SF19 P3).
     *
     * Carries the two instants and the attempt count — never the recipient (it is
     * unproven by definition) and never the code. See `PendingEmailChallengeSchema`.
     */
    pendingEmailChallenge: PendingEmailChallengeSchema.nullable(),
    /** True once the at-accept saga has completed for this session. */
    signed: z.boolean(),
    /**
     * The anonymous status-page handle for THIS session's completed enrollment, or
     * null when nothing has been signed yet.
     *
     * ── WHY THIS IS HERE, AND WHY IT IS NOT AN OMISSION LIKE THE FIELDS ABOVE ──
     * `signed: true` told a returning tab that the enrollment was done and gave it
     * nowhere to go. The token was minted at accept, returned once in the
     * `AcceptResult`, and held ONLY in the SPA's in-memory mutation result — so a
     * refresh, a crash or a closed tab left the customer holding a signed contract
     * with no route to it. The BFF has had it all along (`sign-session.ts`, inside
     * the cached `AcceptResult`), which is why this costs no extra read: the funnel
     * handler ALREADY loads that record to derive `signed`, then throws the rest away.
     *
     * ⚠️ IT IS NOT A CREDENTIAL AND IT IS NOT ENUMERABLE. It is the same opaque,
     * unguessable, single-purpose handle `AcceptResult` already returns across this
     * same boundary — not the quote id, not the account id, not the tenant. It comes
     * back only to the signed session that minted it, and only for the meter that
     * session is currently on (the read is candidate-scoped — the QA-2026-08-06
     * guard). It does not trip `response-guard.ts`: that rule is anchored on exact
     * names (`accessToken`/`serviceToken`/`idToken`/…), and `AcceptResultSchema`
     * already declares this field on `POST /bff/sign/accept` with no allow-list entry.
     */
    statusToken: z.string().min(1).nullable(),
    /**
     * E-CRM04 — true when THIS session was rebuilt from an emailed "resume your
     * enrollment" link (`GET /bff/resume/:token`), so the shop can say "welcome
     * back". A render hint only: it grants nothing, and the steps it restored are
     * evidenced by the fields above exactly as for any other session.
     */
    resumed: z.boolean(),
    /**
     * E-CRM04 — what the resume link restored: `full` (proven email — meter,
     * contact and plan are back) or `progress` (the email was never proven — only
     * the plan and usage are back; the lead enters their address and verifies the
     * email again). `null` when the session was not resumed. A render hint only.
     */
    resumeScope: z.enum(['full', 'progress']).nullable(),
    /**
     * E-CRM04 — a resume link to an UNVERIFIED email is holding the lead's details
     * server-side until they confirm their phone number (`/resume/confirm`).
     * Nothing held is exposed here — only that the check is outstanding.
     */
    resumePending: z.boolean(),
    /**
     * E-CRM04 — a resume link's token is held server-side awaiting the Continue
     * press on `/resume/continue`. The token itself is never exposed.
     */
    resumeTokenHeld: z.boolean(),
});
//# sourceMappingURL=funnel.js.map